import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { US } from 'country-flag-icons/react/3x2';
import style from '../../style/components/wallet/WalletLegalIdCard.module.scss';
import passportBackground from '../../img/wallet-documents/passport-background.svg';
import passportCardBackground from '../../img/wallet-documents/passport-card-background.svg';
import residentCardBackground from '../../img/wallet-documents/resident-card-background-3.svg';

// Scoped to v1 (US Passport, US Passport Card, Immigration Card, SSN card)

const VARIANTS = {
  passportUS: {
    titleKey: 'walletLegalIdCard.passportUS',
    pattern: passportBackground,
    patternClassName: style.residentCardBackground,
    cardClassName: style.passportBg,
    flag: US,
    flagLabelKey: 'walletLegalIdCard.flagUnitedStates',
  },
  passportCardUS: {
    titleKey: 'walletLegalIdCard.passportCardUS',
    pattern: passportCardBackground,
    patternClassName: style.residentCardBackground,
    cardClassName: style.passportCardBg,
    themeClassName: style.themeBlue,
    flag: US,
    flagLabelKey: 'walletLegalIdCard.flagUnitedStates',
  },
  permanentResidentCard: {
    titleKey: 'walletLegalIdCard.permanentResidentCard',
    pattern: residentCardBackground,
    patternClassName: style.residentCardBackground,
    cardClassName: style.residentCardBg,
    flag: US,
    flagLabelKey: 'walletLegalIdCard.flagUnitedStates',
  },
};

// Optional onClick makes the card an interactive element (used in the
// Active IDs list); omitted when reused as a static preview (e.g. inside
// the detail overlay's own card pane).
const interactiveProps = (onClick) =>
  onClick
    ? {
        onClick,
        role: 'button',
        tabIndex: 0,
        onKeyDown: (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick(e);
          }
        },
      }
    : {};

// Pulled from the credential itself (what's printed on the actual
// document), not the account's own profile name -- the account name is a
// separate, optional field plenty of accounts never fill in, while this is
// core data extracted from the verified document whenever a credential
// successfully comes back.
const findRowValue = (rows, labelKey) =>
  rows?.find((row) => row.labelKey === labelKey)?.value;

const WalletLegalIdCard = ({
  variant,
  onClick,
  hideSubtitle,
  showCardHolderName,
}) => {
  const { t } = useTranslation();
  const personalInfo = useSelector(
    (state) => state.countryWorkProfile.legalIdDetails[variant]?.personalInfo,
  );
  const documentVariant = VARIANTS[variant];

  if (!documentVariant) {
    return null;
  }

  const firstName = findRowValue(
    personalInfo,
    'walletLegalIdDetails.nameFirst',
  );
  const lastName = findRowValue(personalInfo, 'walletLegalIdDetails.nameLast');
  const cardHolderName =
    firstName || lastName
      ? `${firstName || ''} ${lastName || ''}`.trim()
      : null;

  const {
    titleKey,
    pattern,
    patternClassName,
    cardClassName,
    themeClassName,
    flag: Flag,
    flagLabelKey,
  } = documentVariant;

  return (
    <div
      className={`${style.card} ${cardClassName} ${
        onClick ? style.clickable : ''
      }`}
      {...interactiveProps(onClick)}
    >
      <img
        alt=""
        className={`${style.pattern} ${patternClassName}`}
        src={pattern}
      />
      <div className={`${style.content} ${themeClassName || ''}`}>
        <div className={style.topRow}>
          <div className={style.titleGroup}>
            <p className={style.title}>{t(titleKey)}</p>
            {!hideSubtitle && (
              <p className={style.subtitle}>{t('shared.verifiedByIdMe')}</p>
            )}
          </div>
          {Flag && <Flag className={style.flag} title={t(flagLabelKey)} />}
        </div>
        {showCardHolderName && cardHolderName && (
          <p className={style.cardHolderName}>{cardHolderName}</p>
        )}
      </div>
    </div>
  );
};

WalletLegalIdCard.propTypes = {
  variant: PropTypes.oneOf(Object.keys(VARIANTS)).isRequired,
  onClick: PropTypes.func,
  hideSubtitle: PropTypes.bool,
  showCardHolderName: PropTypes.bool,
};

WalletLegalIdCard.defaultProps = {
  onClick: undefined,
  hideSubtitle: false,
  showCardHolderName: false,
};

export default memo(WalletLegalIdCard);
